import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FaSearch } from 'react-icons/fa';
import { VscChromeClose } from 'react-icons/vsc';

import { useSearch } from 'hooks/search';
import { COLORS } from 'styles/constants';

import {
  CloseSearchButton,
  Container,
  SearchContainer,
  SearchInput,
  Suggestion,
  Suggestions,
} from './styles';

interface AutoCompleteSearchProps {
  suggestions: string[];
  setSearchIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
}

const placeHolders = [
  'Conserto ar condicionado...',
  'Eletricista...',
  'Pintor de parede...',
  'Encanador...',
].sort(() => 0.5 - Math.random());

const AutoCompleteSearch: React.FC<AutoCompleteSearchProps> = ({
  suggestions,
  setSearchIsFocused,
  setSearchText,
}) => {
  const { selectedSearch } = useSearch();

  // The active selection's index
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  // The suggestions that match the user's input
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  // Whether or not the suggestion list is shown
  const [showSuggestions, setShowSuggestions] = useState(false);
  // What the user has entered
  const [userInput, setUserInput] = useState('');

  const onSearchChange = useCallback(
    (e) => {
      const input = e.currentTarget.value;

      const charsToRemove = /[\u0300-\u036f]/g;

      // Filter our suggestions that don't contain the user's input
      const currentFilteredSuggestions = suggestions.filter(
        (suggestion) =>
          suggestion
            .toLowerCase()
            .normalize('NFD')
            .replace(charsToRemove, '')
            .indexOf(
              input.toLowerCase().normalize('NFD').replace(charsToRemove, ''),
            ) > -1,
      );

      setSearchIsFocused(true);
      setActiveSuggestion(0);
      setFilteredSuggestions(currentFilteredSuggestions);
      setShowSuggestions(true);
      setUserInput(e.currentTarget.value);
    },
    [
      suggestions,
      setSearchIsFocused,
      setActiveSuggestion,
      setFilteredSuggestions,
      setShowSuggestions,
      setUserInput,
    ],
  );

  const onSearchClick = useCallback(
    (e) => {
      setSearchText(e.currentTarget.innerText);
      setActiveSuggestion(0);
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setUserInput(e.currentTarget.innerText);
    },
    [
      setActiveSuggestion,
      setFilteredSuggestions,
      setShowSuggestions,
      setUserInput,
      setSearchText,
    ],
  );

  const onSearchKeyDown = useCallback(
    (e) => {
      // User pressed the enter key
      if (e.keyCode === 13) {
        if (activeSuggestion >= 0) {
          setSearchText(filteredSuggestions[activeSuggestion]);
        } else {
          setSearchText(userInput);
        }
        setActiveSuggestion(0);
        setFilteredSuggestions([]);
        setShowSuggestions(false);
        setUserInput(filteredSuggestions[activeSuggestion]);
      }
      // User pressed the up arrow
      else if (e.keyCode === 38) {
        if (activeSuggestion === 0) {
          setActiveSuggestion(3);
          return;
        }

        setActiveSuggestion((suggestion) => suggestion - 1);
      }
      // User pressed the down arrow
      else if (e.keyCode === 40) {
        if (activeSuggestion === 3) {
          setActiveSuggestion(0);
          return;
        }

        setActiveSuggestion((suggestion) => suggestion + 1);
      }
    },
    [
      userInput,
      filteredSuggestions,
      activeSuggestion,
      setSearchText,
      setFilteredSuggestions,
      setActiveSuggestion,
      setShowSuggestions,
      setUserInput,
    ],
  );

  useEffect(() => {
    if (selectedSearch) {
      setUserInput(selectedSearch.searchTerm);
    }
  }, [setUserInput, selectedSearch, setSearchText]);

  const suggestionsListComponent = useMemo(() => {
    if (showSuggestions && userInput) {
      if (filteredSuggestions.length) {
        return (
          <Suggestions>
            {filteredSuggestions.slice(0, 4).map((suggestion, index) => (
              <Suggestion
                key={suggestion}
                active={index === activeSuggestion}
                type="button"
                onClick={onSearchClick}
              >
                {suggestion}
              </Suggestion>
            ))}
          </Suggestions>
        );
      }

      setActiveSuggestion(-1);

      return (
        <Suggestions>
          <Suggestion active type="button" onClick={onSearchClick}>
            {userInput}
          </Suggestion>
        </Suggestions>
      );
    }
    return <></>;
  }, [
    filteredSuggestions,
    activeSuggestion,
    showSuggestions,
    userInput,
    onSearchClick,
  ]);

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          placeholders={placeHolders}
          type="text"
          onChange={onSearchChange}
          onKeyDown={onSearchKeyDown}
          value={userInput}
        />
        {userInput && userInput.length > 0 ? (
          <CloseSearchButton
            title="Clear"
            type="reset"
            onClick={() => {
              setUserInput('');
              setSearchText('');
            }}
          >
            <VscChromeClose color={COLORS.primary} size={30} />
          </CloseSearchButton>
        ) : (
          <FaSearch color={COLORS.primary} size={30} />
        )}
      </SearchContainer>
      {suggestionsListComponent}
    </Container>
  );
};

export default AutoCompleteSearch;
